// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// ----------------------------------------------------------------------- imports internos
import { useAuth } from "../auth/AuthContext";
import {
  completarDominio,
  DOMINIO_EMPRESA,
  esMailValido,
  normalizarMail,
  MENSAJE_DE_BAJA,
  MENSAJE_NO_AUTORIZADO,
  MENSAJE_SIN_CUENTA,
  mensajeCorreoParecido,
} from "../auth/dominios";
import TransicionCarga from "./TransicionCarga";
import { destinoDespuesDelLogin } from "../functions/routesFunctions";
import { precalentar } from "./precalentar";
import "./css/Login.css";
// El ingreso también se viste con el tema (UAT). Las hojas se importan acá
// porque esta pantalla se ve ANTES que el armazón, que es quien las traía.
import "../assets/css/temas.css";
import "../assets/css/temas-clubes.css";
import "../assets/css/armazon-uat.css";
import {
  TEMA_ESTANDAR,
  temaDeLaOpcion,
  temaGuardado,
  temaOscuro,
  varianteDeLaOpcion,
} from "../components/layout/temasUat";
import CampoClave from "../components/funcionales/CampoClave";
import IngresoQr from "../components/acceso/IngresoQr";
import { canjear, entrarConLlave, fueCancelada } from "../services/acceso";
import {
  esCompu,
  llavesDisponibles,
  marcarRecienEntro,
  tieneLlave,
} from "../shared/acceso";

/* Ingreso en dos pasos: primero el correo, después la contraseña.
 *
 * El paso extra no es adorno. Con el correo en la mano se sabe en cuál de
 * cuatro situaciones está la persona, y se le puede decir exactamente qué
 * hacer — en vez de mostrarle un campo de contraseña que quizá no tiene, que
 * es la forma más rápida de que concluya "esto no anda" y cierre la pestaña.
 *
 * Los estados salen de `estado_de_acceso` en Supabase:
 *   no_autorizado   no está en la lista
 *   sin_cuenta      habilitado, pero nadie le dio de alta todavía
 *   clave_temporal  tiene una clave provisoria que va a tener que cambiar
 *   existente       tiene su propia clave
 *   de_baja         la cuenta existe y la dieron de baja (05/10): ni se le
 *                   pide la clave, que Supabase igual no le aceptaría
 *
 * Quién puede entrar NO se decide acá: sale de la tabla `accesos_permitidos`.
 *
 * ---------------------------------------------------------------------------
 * LA PRESENTACIÓN sale del prototipo "FiBOT Login a Carga" de Claude Design:
 * panel blanco a la derecha, lado grafito a la izquierda con el bitren, y al
 * enviar el formulario el panel se corre, el logo se centra y aparece la ruta
 * Buenos Aires → Ushuaia dibujándose mientras se resuelve.
 *
 * Los CUATRO estados viven en la MISMA tarjeta: no hay una pantalla por paso,
 * cambia el contenido. Así la transición es continua y no quedan dos maquetas
 * parecidas que se vayan separando con el tiempo.
 *
 * LA ANIMACIÓN CORRE DESPUÉS DE LA CONTRASEÑA, no del correo. El chequeo del
 * correo es una consulta y contesta en menos de un segundo: taparlo con cuatro
 * segundos de ruta sería frenar a la persona a propósito. En cambio al aceptar
 * la contraseña arranca `iniciarRealtime()`, que se suscribe a catorce
 * colecciones — ESA es la espera que existe de verdad, y es la que la ruta
 * tapa.
 *
 * Y se aprovecha: mientras se dibuja se precargan el caché, los permisos, los
 * conteos y —para quien tiene el mapa— la sesión en FlotaBot. Ver
 * `precalentar.js`.
 *
 * El tramo principal de la animación se completa SIEMPRE, aunque la carga
 * termine antes: es el único momento en que se puede precargar sin competir
 * con una pantalla que la persona esté mirando.
 */

/* SIN CLAVE (Igna, 02/11): en la compu lo primero es el QR, que se aprueba
 * desde el celular. Abajo, si en este navegador ya se creó una llave de
 * acceso, el botón para entrar con ella. La contraseña sigue ahí, siempre:
 * no todos tienen el hub en el celular. En el celular no hay QR (¿con qué lo
 * escanearía?), sólo la llave si ya tiene. */

const MS_PANEL_SALE = 380;
const MS_APARECE_MAPA = 900;

const Login = () => {
  const { login, consultarAcceso, buscarCorreoParecido, recuperarPassword } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [paso, setPaso] = useState("correo");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [esProvisoria, setEsProvisoria] = useState(false);
  const [error, setError] = useState("");
  const [sugerido, setSugerido] = useState("");
  const [cargando, setCargando] = useState(false);
  const compu = useRef(esCompu()).current;
  const conLlave = useRef(llavesDisponibles() && tieneLlave()).current;

  // La transición: el panel se corre (`va`), aparece el mapa (`ruta`) y al
  // final se funde todo (`terminando`).
  const [va, setVa] = useState(false);
  const [ruta, setRuta] = useState(false);
  const [terminando, setTerminando] = useState(false);

  // Se entra cuando se cumplen LAS DOS: que la animación haya hecho su tramo
  // principal, y que el precalentado haya terminado. La que tarde más manda.
  const [pisoCumplido, setPisoCumplido] = useState(false);
  const [precalentado, setPrecalentado] = useState(false);
  const relojes = useRef([]);

  const limpiarRelojes = () => {
    relojes.current.forEach(clearTimeout);
    relojes.current = [];
  };

  // Al desmontar no queda ningún reloj andando: si la pantalla se va antes de
  // que la transición termine, esos timeouts intentarían tocarle el estado a
  // un componente que ya no existe.
  useEffect(() => limpiarRelojes, []);

  const arrancarTransicion = () => {
    limpiarRelojes();
    relojes.current.push(setTimeout(() => setVa(true), MS_PANEL_SALE));
    relojes.current.push(setTimeout(() => setRuta(true), MS_APARECE_MAPA));
  };

  // Trae el panel de vuelta. Se usa para los dos desenlaces que no entran a la
  // app: un error, y el paso del correo a la clave.
  const volverAlPanel = () => {
    limpiarRelojes();
    setVa(false);
    setRuta(false);
    // Rebobinar también las banderas: sin esto, un segundo intento entraría de
    // una, sin animación y sin haber precargado nada.
    setPisoCumplido(false);
    setPrecalentado(false);
  };

  const volverAlCorreo = () => {
    setPaso("correo");
    setPassword("");
    setError("");
    setSugerido("");
    setEsProvisoria(false);
    volverAlPanel();
  };

  // Recién acá se entra: cuando la ruta llegó al final y el fundido terminó.
  // A donde iba antes de que le pidieran entrar (`PrivateRoute`), si era una
  // dirección del hub; si no, al inicio.
  const entrarALaApp = useCallback(() => {
    navigate(destinoDespuesDelLogin(location.state), { replace: true });
  }, [navigate, location.state]);

  const marcarPiso = useCallback(() => setPisoCumplido(true), []);

  useEffect(() => {
    if (pisoCumplido && precalentado) setTerminando(true);
  }, [pisoCumplido, precalentado]);

  // ── paso 1: el correo ───────────────────────────────────────────────────
  // Recibe el mail por parámetro en vez de leerlo del estado: el botón de la
  // sugerencia lo llama con una dirección que se acaba de escribir, y leyendo
  // `email` vería todavía la anterior.
  const evaluarCorreo = async (mail) => {
    setError("");
    setSugerido("");

    if (!esMailValido(mail)) {
      setError("Escribí un correo válido.");
      return;
    }

    setCargando(true);
    try {
      const estado = await consultarAcceso(mail);

      if (estado === "no_autorizado") {
        setError(MENSAJE_NO_AUTORIZADO);
        return;
      }
      if (estado === "de_baja") {
        setError(MENSAJE_DE_BAJA);
        return;
      }
      if (estado === "sin_cuenta") {
        // Antes de mandarlo a pedir un alta, mirar si la cuenta ya existe y lo
        // que falló fue el tipeo. La lista de accesos habilita el dominio
        // entero, así que las dos cosas llegan hasta acá indistinguibles.
        const parecido = await buscarCorreoParecido(mail);
        if (parecido) {
          setSugerido(parecido);
          setError(mensajeCorreoParecido(parecido));
        } else {
          setError(MENSAJE_SIN_CUENTA);
        }
        return;
      }

      // clave_temporal y existente van los dos al campo de contraseña: en
      // ambos casos la persona tiene una y la tiene que escribir. Lo único que
      // cambia es el texto, para que quien recibió una provisoria sepa que es
      // esa la que va y que después se la van a hacer cambiar.
      setEsProvisoria(estado === "clave_temporal");
      setPaso("clave");
    } catch (err) {
      setError("No se pudo continuar. Revisá tu conexión y volvé a intentar.");
    } finally {
      setCargando(false);
    }
  };

  const continuarConCorreo = (e) => {
    e.preventDefault();
    // Sin @, se completa con el dominio de la empresa — y se escribe en el
    // campo, así el paso de la clave muestra la dirección con la que se entra.
    const mail = completarDominio(email);
    setEmail(mail);
    return evaluarCorreo(mail);
  };

  const usarElSugerido = () => {
    setEmail(sugerido);
    return evaluarCorreo(sugerido);
  };

  // ── paso 2: la contraseña ───────────────────────────────────────────────
  const entrar = async (e) => {
    e.preventDefault();
    setError("");
    setCargando(true);
    arrancarTransicion();
    try {
      await login(normalizarMail(email), password);
      if (compu) marcarRecienEntro();
      // Si la contraseña era provisoria, la ruta privada se encarga de mandar
      // a /cambiar-password. Acá no hace falta decidir nada.
      //
      // El precalentado NO se espera acá: se deja corriendo y el efecto de
      // arriba decide cuándo entrar. Si se esperara con un await, el `finally`
      // apagaría el "cargando" recién al final y el botón quedaría en
      // "Entrando" con el panel ya afuera de la pantalla.
      precalentar().finally(() => setPrecalentado(true));
    } catch (err) {
      /* Una cuenta dada de baja tiene el ingreso cortado en Supabase Auth
       * (`user_banned`). Decirle «contraseña incorrecta» la mandaría a
       * recuperar una clave que no le va a servir. */
      const deBaja =
        err?.code === "user_banned" || /banned/i.test(err?.message || "");
      setError(
        deBaja
          ? MENSAJE_DE_BAJA
          : esProvisoria
          ? "La contraseña provisoria no coincide. Revisá que la hayas copiado completa."
          : "Correo o contraseña incorrectos.",
      );
      /* LO ESCRITO SE QUEDA. Antes se borraba, y con el ojito (CampoClave)
       * no había nada que mirar: lo que sirve después de «incorrectos» es ver
       * QUÉ se mandó y corregir esa letra, no volver a tipear a ciegas (Igna,
       * 09/10, desde el celular). */
      volverAlPanel();
    } finally {
      setCargando(false);
    }
  };

  // ── sin clave: el QR aprobado desde el celular, o la llave ──────────────
  // Las dos terminan en una sesión común; desde ahí, la misma transición que
  // con la contraseña.
  const entrarSinClave = async (hacer) => {
    if (cargando) return;
    setError("");
    setCargando(true);
    try {
      await hacer();
      if (compu) marcarRecienEntro();
      arrancarTransicion();
      precalentar().finally(() => setPrecalentado(true));
    } catch (err) {
      if (!fueCancelada(err)) {
        setError(
          err?.codigo === "espera"
            ? "Demasiados intentos. Esperá unos minutos o entrá con tu contraseña."
            : err?.codigo === "desconocida"
            ? "Esa llave ya no está registrada. Entrá con tu contraseña y creá otra."
            : "No se pudo entrar. Probá de nuevo o entrá con tu contraseña.",
        );
      }
      volverAlPanel();
    } finally {
      setCargando(false);
    }
  };

  const conQr = useCallback(
    (tokenHash) => entrarSinClave(() => canjear(tokenHash)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const olvideLaClave = async () => {
    try {
      await recuperarPassword(normalizarMail(email));
      Swal.fire({
        title: "Te mandamos el correo",
        text: "Revisá tu bandeja de entrada para volver a definir tu contraseña. Si no aparece, mirá en correo no deseado.",
        icon: "success",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#f5871f",
      });
    } catch (err) {
      Swal.fire({
        title: "No se pudo enviar",
        text: "Probá de nuevo en un minuto, o pedile a sistemas una contraseña provisoria.",
        icon: "error",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#f5871f",
      });
    }
  };

  /* EL TEMA EN EL INGRESO (UAT). Igna, 06/10: «me gusta la idea de la pantalla
     de inicio con el tema». Acá todavía no se sabe quién entra, así que es el
     último tema con el que se usó el hub en este navegador. El armazón lo
     saca de la raíz al salir; por eso se vuelve a poner cada vez. */
  const tema = useRef(temaGuardado()).current;
  useEffect(() => {
    // Estándar es el ingreso de siempre: sin atributo no se viste nada. Una
    // variante («Venom (cara)») pone su tema y la variante al lado, que es lo
    // que deja el ingreso de la cara limpio, sin goo.
    if (tema === TEMA_ESTANDAR) return;
    const raiz = document.documentElement;
    raiz.setAttribute("data-tema", temaDeLaOpcion(tema));
    const variante = varianteDeLaOpcion(tema);
    if (variante) raiz.setAttribute("data-variante", variante);
    else raiz.removeAttribute("data-variante");
  }, [tema]);

  const clases = [
    "fi-login",
    temaOscuro(tema) && "fi-oscuro",
    va && "va",
    ruta && "ruta",
    terminando && "listo",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={clases}>
      <div className="fi-pane">
        <div className="fi-card">
          {paso === "correo" ? (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <h2>Iniciar sesión</h2>
                {compu && <p className="fi-sub">Con el QR, desde el celular.</p>}
              </div>
              {compu && <IngresoQr onListo={conQr} deshabilitado={cargando} />}
              {conLlave && (
                <button
                  type="button"
                  className="fi-btn fi-btn--llave"
                  onClick={() => entrarSinClave(entrarConLlave)}
                  disabled={cargando}
                >
                  🔑 Entrar con llave de acceso
                </button>
              )}
              {(compu || conLlave) && (
                <div className="fi-o">
                  <span>o con tu correo y contraseña</span>
                </div>
              )}
              {!compu && !conLlave && (
                <p className="fi-sub">Ingresá con tu correo de la empresa.</p>
              )}
              <form
                onSubmit={continuarConCorreo}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div className="fi-campo">
                  <label htmlFor="login-email">Correo</label>
                  <input
                    id="login-email"
                    type="email"
                    /* El dominio va por la constante y no escrito de nuevo:
                       eran dos copias del mismo dato y la empresa pasó a
                       tener DOS dominios el 15/09/2026. El placeholder sigue
                       mostrando el de la mayoría, igual que `completarDominio`
                       —los dos están en el cartel de «no tenés acceso»—. */
                    placeholder={`nombre${DOMINIO_EMPRESA}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    disabled={cargando}
                    autoFocus={!compu}
                    required
                  />
                </div>
                {error && <p className="fi-err">{error}</p>}
                {sugerido && (
                  <button type="button" className="fi-link" onClick={usarElSugerido}>
                    Entrar como {sugerido}
                  </button>
                )}
                <button type="submit" className="fi-btn" disabled={cargando}>
                  {cargando && <span className="fi-spin" />}
                  {cargando ? "Verificando" : "Continuar"}
                </button>
              </form>
            </>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <h2>{esProvisoria ? "Primer ingreso" : "Hola de nuevo"}</h2>
                <p className="fi-sub fi-mail">{normalizarMail(email)}</p>
              </div>
              {esProvisoria && (
                <p className="fi-texto">
                  Escribí la contraseña provisoria que te pasaron. Apenas entres
                  vas a poder definir la tuya.
                </p>
              )}
              <form
                onSubmit={entrar}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div className="fi-campo">
                  <label htmlFor="login-password">
                    {esProvisoria ? "Contraseña provisoria" : "Contraseña"}
                  </label>
                  <CampoClave
                    id="login-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={cargando}
                    autoFocus
                    required
                  />
                </div>
                {error && <p className="fi-err">{error}</p>}
                <button type="submit" className="fi-btn" disabled={cargando}>
                  {cargando && <span className="fi-spin" />}
                  {cargando ? "Entrando" : "Entrar"}
                </button>
              </form>
              <div className="fi-acciones">
                <button type="button" className="fi-link" onClick={volverAlCorreo}>
                  Cambiar de correo
                </button>
                {!esProvisoria && (
                  <button type="button" className="fi-link" onClick={olvideLaClave}>
                    Olvidé mi contraseña
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <TransicionCarga
        dibujando={ruta}
        terminando={terminando}
        onFin={entrarALaApp}
        onPisoCumplido={marcarPiso}
      />
    </div>
  );
};

export default Login;
